import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export function usePermissions() {
  const { activeWorkspaceId } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-permissions", activeWorkspaceId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_my_permissions" as never);
      if (error) throw error;
      return (data ?? []) as string[];
    },
    enabled: !!activeWorkspaceId,
    staleTime: 5 * 60 * 1000,
  });

  const permissions = useMemo(
    () => (data ? new Set(data) : null),
    [data]
  );

  const can = useMemo(() => {
    return (key: string): boolean => {
      if (isLoading || isError || permissions === null) return true;
      return permissions.has(key);
    };
  }, [isLoading, isError, permissions]);

  const firstAllowedPath = useMemo(() => {
    const order: ReadonlyArray<[string, string]> = [
      ["dashboard", "/app/dashboard"],
      ["conversations", "/app/conversations"],
      ["contacts", "/app/contacts"],
      ["campaigns.text", "/app/campaigns"],
      ["campaigns.blueprints", "/app/campaign-blueprints"],
      ["campaigns.blueprints.reports", "/app/reports/campaign-blueprints"],
      ["reports", "/app/reports"],
      ["automations", "/app/automations"],
      ["whatsapp_templates", "/app/whatsapp-templates"],
      ["updates", "/app/updates"],
      ["help", "/app/help"],
      ["support", "/app/support"],
      ["settings", "/app/settings/labels"],
    ];
    const match = order.find(([key]) => can(key));
    return match ? match[1] : "/app/conversations";
  }, [can]);

  return { can, permissions, isLoading, isError, firstAllowedPath };
}
